import type { AgentAbout } from '@ralph/contracts'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Ban,
  BookOpen,
  Brain,
  Check,
  CircleCheck,
  Eye,
  EyeOff,
  Info,
  Minus,
  Route,
  ShieldCheck,
  Zap
} from 'lucide-react'
import type { ReactNode } from 'react'
import { AskManagers, type AskState } from '@/components/marketplace/consumer/AskManagers.tsx'
import { OwnerLabel } from '@/components/marketplace/consumer/OwnerLabel.tsx'
import { ASK_AGAIN_AFTER_MS } from '@/components/marketplace/maker/knowledge-coverage.ts'
import { Badge } from '@/components/ui/primitives.tsx'
import { marketplaceKeys } from '@/hooks/use-marketplace.ts'
import { useT } from '@/i18n/index.ts'

/**
 * The disclosure panel (AGM-003).
 *
 * Every line below is a rendering of `about.disclosure`, which the server computes from the
 * RESOLVED definition. There is deliberately no prop on this component that a maker can
 * write: if a maker could type into this panel it would become marketing, and integrity rule
 * 1 — "no surface may describe a restriction the runtime does not enforce" — would have no
 * mechanical backing.
 *
 * Coverage is per reader and computed per request (PRD-26): two people reading the same listing
 * see different knowledge lines, because they can use different bases. Three states, each said
 * about the reader — they can use it; it answers through this agent for them although they cannot
 * open it (its managers allowed that, D-16); or it is not available to them and the agent answers
 * them without it, with **Ask its managers** (D-17). Abilities behind a system limited to groups
 * the reader is not in read "Not available to you" (GRP-062).
 *
 * **The layout is four cards, and they are all open.** Nothing is collapsed, tabbed, or put
 * behind "show more": a restriction a reader has to click to see is a restriction they will
 * decide without, which is the failure the whole panel exists to prevent.
 */
export const DisclosurePanel = ({
  disclosure,
  agentId,
  agentName
}: {
  disclosure: AgentAbout['disclosure']
  agentId: string
  agentName: string
}) => {
  const client = useQueryClient()
  // Both packs are read in the body. A capture at import would leave this panel — the one
  // surface whose whole job is telling a reader the truth about an agent — in the language
  // that happened to be active when the module loaded (PRD-27 F-3, LOC-012).
  const messages = useT().marketplace.gallery
  const t = messages.disclosure
  const gaps = disclosure.knowledge.filter((base) => base.coverage === 'none')
  const hasKnowledge = disclosure.knowledge.length > 0
  const remembers = disclosure.memory.usesSavedPreferences || disclosure.memory.learnsHere
  // `already_permitted`: the server has since decided this reader gets the base after all. The
  // listing is cached per reader, so it is read again rather than corrected here by guesswork.
  const refresh = () => void client.invalidateQueries({ queryKey: marketplaceKeys.about(agentId) })

  return (
    <section>
      <header className="flex items-start gap-2">
        <ShieldCheck size={13} className="mt-[3px] shrink-0 text-grounded" aria-hidden />
        <div className="min-w-0">
          {/* Sans, not the display serif: this opens a group of cards, not a screen. */}
          <h2 className="font-sans text-[14px] font-semibold tracking-[-0.018em]">{t.title}</h2>
          <p className="mt-0.5 text-pretty text-[12.5px] leading-relaxed text-fg-subtle">{t.subtitle}</p>
        </div>
      </header>

      <div className="mt-3.5 grid gap-3 md:grid-cols-2">
        <DisclosureCard
          icon={<BookOpen size={13} strokeWidth={2.1} className="text-grounded" aria-hidden />}
          title={t.knowledgeTitle}
        >
          <p className="text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
            {disclosure.knowledgeMode === 'attached' ? t.knowledgeModeAttached : t.knowledgeModeAll}
          </p>

          {hasKnowledge && (
            <ul className="mt-2.5 flex flex-col gap-3">
              {disclosure.knowledge.map((base) => (
                // Each state differs in shape before it differs in hue — a tick, a route, a
                // warning triangle — so a colour-blind reader reads the same three states.
                <Line key={base.baseId} icon={coverageIcon(base.coverage)}>
                  <span className="block font-medium leading-snug">{base.name}</span>
                  <span className="mt-0.5 flex min-w-0 items-center gap-1 text-[11.5px] text-fg-subtle">
                    <span className="shrink-0">{messages.about.ownerLabel}</span>
                    <OwnerLabel owner={base.owner} size={16} className="text-fg-muted" />
                  </span>
                  <span
                    className={
                      base.coverage === 'none'
                        ? 'mt-1 block text-[12px] leading-snug text-alert'
                        : 'mt-1 block text-[12px] leading-snug text-fg-muted'
                    }
                  >
                    {base.coverage === 'full'
                      ? t.coverage.full
                      : base.coverage === 'lent'
                        ? t.coverage.lent(agentName)
                        : t.coverage.none(agentName)}
                  </span>
                  {base.coverage === 'none' && (
                    <AskManagers
                      baseId={base.baseId}
                      baseName={base.name}
                      agentId={agentId}
                      agentName={agentName}
                      ask={askStateOf(base.ask)}
                      noMaintainer={base.noMaintainer}
                      onOutOfDate={refresh}
                    />
                  )}
                </Line>
              ))}
            </ul>
          )}

          {!hasKnowledge && disclosure.knowledgeMode === 'attached' && (
            <ul className="mt-2.5">
              <Line icon={<Ban size={12} strokeWidth={2.4} className="text-fg-subtle" aria-hidden />}>
                {t.knowledgeEmpty}
              </Line>
            </ul>
          )}

          {hasKnowledge && (
            <p className="mt-3 flex items-start gap-1.5 text-[12px] leading-snug">
              {gaps.length === 0 ? (
                <>
                  <Check size={12} strokeWidth={2.6} className="mt-[2px] shrink-0 text-grounded" aria-hidden />
                  <span className="text-fg-muted">{t.coverageFull}</span>
                </>
              ) : (
                <>
                  <AlertTriangle size={12} strokeWidth={2.4} className="mt-[2px] shrink-0 text-alert" aria-hidden />
                  <span className="text-alert">{t.coveragePartial}</span>
                </>
              )}
            </p>
          )}
        </DisclosureCard>

        <DisclosureCard
          icon={<Zap size={13} strokeWidth={2.1} className="text-primary" aria-hidden />}
          title={t.capabilitiesTitle}
        >
          {disclosure.capabilities.length === 0 ? (
            <ul>
              <Line icon={<Ban size={12} strokeWidth={2.4} className="text-fg-subtle" aria-hidden />}>
                {t.capabilitiesEmpty}
              </Line>
            </ul>
          ) : (
            <ul className="flex flex-col gap-3">
              {disclosure.capabilities.map((capability) => (
                <Line
                  key={capability.name}
                  icon={
                    !capability.available ? (
                      <EyeOff size={12} strokeWidth={2.2} className="text-fg-subtle" aria-hidden />
                    ) : capability.wording === 'acts' ? (
                      <Zap size={12} strokeWidth={2.4} className="text-primary" aria-hidden />
                    ) : (
                      <Eye size={12} strokeWidth={2.2} className="text-fg-subtle" aria-hidden />
                    )
                  }
                >
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span
                      className={
                        capability.available
                          ? 'text-[12.5px] font-semibold leading-snug'
                          : 'text-[12.5px] font-semibold leading-snug text-fg-muted'
                      }
                    >
                      {capability.name}
                    </span>
                    {capability.available ? (
                      <Badge tone={capability.wording === 'acts' ? 'primary' : 'neutral'}>
                        {capability.wording === 'acts' ? <Zap size={10} aria-hidden /> : <Eye size={10} aria-hidden />}
                        {capability.wording === 'acts' ? t.wordingActs : t.wordingReads}
                      </Badge>
                    ) : (
                      <Badge>
                        <EyeOff size={10} aria-hidden />
                        {t.notAvailable}
                      </Badge>
                    )}
                  </span>
                  {capability.description && (
                    <p className="mt-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
                      {capability.description}
                    </p>
                  )}
                  {!capability.available && (
                    <p className="mt-1 text-pretty text-[12px] leading-[1.45] text-fg-subtle">{t.notAvailableHint}</p>
                  )}
                </Line>
              ))}
            </ul>
          )}
        </DisclosureCard>

        <DisclosureCard
          icon={
            <Eye
              size={13}
              strokeWidth={2.1}
              className={disclosure.grounded ? 'text-grounded' : 'text-fg-subtle'}
              aria-hidden
            />
          }
          title={t.groundingTitle}
        >
          <ul>
            <Line
              icon={
                disclosure.grounded ? (
                  <CircleCheck size={12} strokeWidth={2.2} className="text-grounded" aria-hidden />
                ) : (
                  <Info size={12} strokeWidth={2.2} className="text-fg-subtle" aria-hidden />
                )
              }
            >
              {disclosure.grounded ? t.groundedOn : t.groundedOff}
            </Line>
          </ul>
        </DisclosureCard>

        {/*
          Two lines, always both (AGM-090). The global one is platform behaviour and stays true
          whatever the maker chose; collapsing them into a single "remembers things" line is the
          exact drift the requirement exists to prevent.
        */}
        <DisclosureCard
          icon={
            <Brain size={13} strokeWidth={2.1} className={remembers ? 'text-primary' : 'text-fg-subtle'} aria-hidden />
          }
          title={t.memoryTitle}
        >
          <ul className="flex flex-col gap-2.5">
            <MemoryLine
              on={disclosure.memory.usesSavedPreferences}
              onText={t.memoryGlobal}
              offText={t.memoryGlobalOff}
            />
            <MemoryLine on={disclosure.memory.learnsHere} onText={t.memoryLearns} offText={t.memoryLearnsOff} />
          </ul>
        </DisclosureCard>
      </div>
    </section>
  )
}

type DisclosureBase = AgentAbout['disclosure']['knowledge'][number]

const coverageIcon = (coverage: DisclosureBase['coverage']): ReactNode =>
  coverage === 'full' ? (
    <Check size={12} strokeWidth={2.6} className="text-grounded" aria-hidden />
  ) : coverage === 'lent' ? (
    <Route size={12} strokeWidth={2.2} className="text-grounded" aria-hidden />
  ) : (
    <AlertTriangle size={12} strokeWidth={2.4} className="text-alert" aria-hidden />
  )

/** The reader's own last ask, in the shape the ask control reads. A withdrawn or closed one is no answer. */
const askStateOf = (ask: DisclosureBase['ask']): AskState => {
  if (ask?.status === 'pending') return { kind: 'pending', at: ask.at }
  if (ask?.status === 'declined') {
    const decided = Date.parse(ask.at)
    return {
      kind: 'declined',
      at: ask.at,
      by: ask.decidedByName,
      note: ask.note,
      canAskAgain: Number.isNaN(decided) || Date.now() - decided >= ASK_AGAIN_AFTER_MS
    }
  }
  return { kind: 'none' }
}

/** One of the four cards. Same frame every time, so no section can look more optional than another. */
const DisclosureCard = ({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) => (
  <div className="card rounded-card p-4">
    <h3 className="eyebrow flex items-center gap-[7px] text-fg-muted">
      {icon}
      {title}
    </h3>
    <div className="mt-3">{children}</div>
  </div>
)

/**
 * A disclosed fact: a glyph, the sentence, and an optional measured note on the right.
 *
 * The glyph is never the whole message — every row reads correctly with the colour and the
 * icon stripped out, which is what AGM-124 asks for and what a printed page or a
 * high-contrast theme actually delivers.
 */
const Line = ({ icon, children, meta }: { icon: ReactNode; children: ReactNode; meta?: string }) => (
  <li className="flex items-start gap-2">
    <span className="mt-[3px] flex-none">{icon}</span>
    <div className="min-w-0 flex-1 text-pretty text-[12.5px] leading-[1.5] text-fg">{children}</div>
    {/* Allowed to shrink and wrap rather than pinned: a long owner name would otherwise
        push the row past the card's edge. */}
    {meta && <span className="readout mt-[3px] max-w-[45%] text-right text-[9.5px] text-fg-subtle">{meta}</span>}
  </li>
)

const MemoryLine = ({ on, onText, offText }: { on: boolean; onText: string; offText: string }) => (
  <Line
    icon={
      on ? (
        <Check size={12} strokeWidth={2.6} className="text-grounded" aria-hidden />
      ) : (
        <Minus size={12} strokeWidth={2.6} className="text-fg-subtle" aria-hidden />
      )
    }
  >
    <span className={on ? undefined : 'text-fg-muted'}>{on ? onText : offText}</span>
  </Line>
)
